import { Stack, Box } from '@mui/material';
import { StatusCodes } from 'http-status-codes';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { scrollIntoView } from 'seamless-scroll-polyfill';
import useSWR from 'swr';

import { axiosInstanceForTenantAPI } from '../../assets/utils/axiosInstance';
import { isRESTAPIError } from '../../assets/utils/helpers';
import QuickstartCompanyDetails from '../../components/customer/quickstart/quickstartCompanyDetails';
import QuickstartInviteTeamMembers from '../../components/customer/quickstart/quickstartInviteTeamMembers';
import QuickstartOnboarding from '../../components/customer/quickstart/quickstartOnboarding';
import QuickstartProgramCommunication from '../../components/customer/quickstart/quickstartProgramCommunication';
import QuickstartProgramCustomization from '../../components/customer/quickstart/quickstartProgramCustomization';
import QuickstartProgramVision from '../../components/customer/quickstart/quickstartProgramVision';
import CustomerQuickstartComponent from '../../components/customer/quickstart/quickstartSideComponent';
import { useAuth } from '../../contexts/auth/authContext';

import LanguageSelector from '~/components/common/language/languageSelector';

interface QuickstartObject {
  companyName: string;
  countryNames: string[];
  hotlineNumber: string;
  industry: string;
  maxSeats: number;
  companyGoal: string;
  programUrl: string;
  logoUrl: string;
  programLaunchMessage: string;
  policyText: string;
}
interface QuickstartObjectFromAPI {
  name: string;
  country_names: string[];
  hotline_number: string;
  industry: string;
  max_seats: number;
  company_goals: string;
  wb_program_url: string;
  logo_url: string;
  program_launch_message: string;
  policy_text: string;
}

const transformDataFromAPI = (data: QuickstartObjectFromAPI) => {
  const {
    name: companyName,
    country_names: countryNames,
    hotline_number: hotlineNumber,
    industry,
    max_seats: maxSeats,
    company_goals: companyGoal,
    wb_program_url: programUrl,
    logo_url: logoUrl,
    program_launch_message: programLaunchMessage,
    policy_text: policyText,
  } = data;
  return {
    industry,
    companyName,
    countryNames,
    hotlineNumber,
    maxSeats,
    companyGoal,
    programUrl,
    logoUrl,
    programLaunchMessage,
    policyText,
  };
};

const checkIfHasOtherUsers = (list: unknown[]) => {
  if (list === undefined || list === null) return false;
  if (list.length === 1) return false;
  else return true;
};

const CustomerQuickStart = () => {
  const navigate = useNavigate();
  const MAX_SCREEN_NUMBER = 5;
  const onboaringScreen = useRef<HTMLElement | null>(null);
  const CompanyDetailsScreen = useRef<HTMLElement | null>(null);
  const ProgramVisionScreen = useRef<HTMLElement | null>(null);
  const InviteTeamMemberScreen = useRef<HTMLElement | null>(null);
  const ProgramCustomizationScreen = useRef<HTMLElement | null>(null);
  const ProgramCommunicationScreen = useRef<HTMLElement | null>(null);
  const [screenNum, setScreenNum] = useState<number>(0);
  const [quickStartData, setQuickStartData] = useState<QuickstartObject>({
    companyName: '',
    countryNames: [],
    hotlineNumber: '',
    industry: '',
    maxSeats: 0,
    companyGoal: '',
    programUrl: '',
    logoUrl: '',
    programLaunchMessage: '',
    policyText: '',
  });

  const { slug } = useAuth();
  const intl = useIntl();

  const [companySlug, setCompanySlug] = useState<string | null>(slug);
  useEffect(() => {
    if (!slug) {
      setCompanySlug(localStorage.getItem('slug'));
    }
  }, [slug]);

  const [requestStatus, setRequestStatus] = useState({
    isLoading: false,
    success: false,
    error: false,
    text: '',
  });

  const changeScreen = (screenNumber: number) => {
    setScreenNum(screenNumber);
  };

  useEffect(() => {
    switch (screenNum) {
      case 0:
        scrollIntoView(onboaringScreen.current as Element, {
          behavior: 'smooth',
          inline: 'center',
          block: 'start',
        });
        break;
      case 1:
        scrollIntoView(CompanyDetailsScreen.current as Element, {
          behavior: 'smooth',
          inline: 'center',
          block: 'start',
        });
        break;
      case 2:
        scrollIntoView(ProgramVisionScreen.current as Element, {
          behavior: 'smooth',
          inline: 'center',
          block: 'start',
        });
        break;
      case 3:
        scrollIntoView(InviteTeamMemberScreen.current as Element, {
          behavior: 'smooth',
          inline: 'center',
          block: 'start',
        });
        break;
      case 4:
        scrollIntoView(ProgramCustomizationScreen.current as Element, {
          behavior: 'smooth',
          inline: 'center',
          block: 'start',
        });
        break;
      case 5:
        scrollIntoView(ProgramCommunicationScreen.current as Element, {
          behavior: 'smooth',
          inline: 'center',
          block: 'start',
        });
        break;
      default:
        break;
    }
  }, [screenNum]);

  const { data: userList } = useSWR(
    companySlug ? `tenant/${companySlug}/users` : '',
    { revalidateOnFocus: false },
  );
  const { data: user, error: userError } = useSWR('/tenant/quickstart', {
    revalidateOnFocus: false,
  });

  useEffect(() => {
    if (user?.has_onboarding_done || userError) navigate('/customer/reports');
  }, [user, navigate, userError]);

  const fetchQuickstartDataHandler = useCallback(async () => {
    setRequestStatus({
      isLoading: true,
      success: false,
      error: false,
      text: '',
    });
    try {
      const response = await axiosInstanceForTenantAPI(companySlug).get(
        '/quickstart-details',
      );
      const status = response.status;
      const data = response.data;
      if (status === 200) {
        setRequestStatus({
          isLoading: false,
          error: false,
          text: '',
          success: true,
        });
        setQuickStartData(transformDataFromAPI(data));
      }
    } catch (err: unknown) {
      let errorMessage = intl.formatMessage({
        defaultMessage: 'Error happened. We\'re currently fixing it.',
      });
      if (isRESTAPIError(err)) {
        if (`${err.response.status}` === String(StatusCodes.NOT_FOUND)) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Department not found on your account',
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.INTERNAL_SERVER_ERROR)
        ) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Error happened in the server',
          });
        }
      }
      setRequestStatus({
        isLoading: false,
        success: false,
        error: true,
        text: errorMessage,
      });
    }
  }, [companySlug, intl]);

  useEffect(() => {
    fetchQuickstartDataHandler();
  }, [fetchQuickstartDataHandler]);

  return !requestStatus.error && (
    <Stack
      direction="row"
    >
      <CustomerQuickstartComponent screenNum={screenNum} />
      <Stack
        flexGrow={1}
        sx={{
          opacity: requestStatus.isLoading ? 0.7 : 1,
          cursor: requestStatus.isLoading ? 'not-allowed' : 'default',
          height: '100vh',
          overflow: 'hidden',
          overscrollBehaviorY: 'contain',
          scrollSnapType: 'y mandatory',
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': {
            width: 'none',
          },
          scrollbarWidth: 'none',
          backgroundColor: '#ffffff',
        }}
      >
        <Box ref={onboaringScreen}>
          <QuickstartOnboarding changeScreen={changeScreen} />
        </Box>
        <Box ref={CompanyDetailsScreen}>
          <QuickstartCompanyDetails
            quickstartData={{
              companyName: quickStartData.companyName ?? '',
              primaryContact: quickStartData.hotlineNumber ?? '',
              operationalRegion: quickStartData.countryNames[0] ?? '',
              industry: quickStartData.industry ?? '',
              companySize: quickStartData.maxSeats ?? 0,
            }}
            slug={
              companySlug
                ? companySlug
                : `${localStorage.getItem('companySlug')}`
            }
            screenNum={screenNum}
            maxScreenNum={MAX_SCREEN_NUMBER}
            changeScreen={changeScreen}
          />
        </Box>
        <Box ref={ProgramVisionScreen}>
          <QuickstartProgramVision
            slug={
              companySlug
                ? companySlug
                : `${localStorage.getItem('companySlug')}`
            }
            goal={quickStartData?.companyGoal}
            screenNum={screenNum}
            maxScreenNum={MAX_SCREEN_NUMBER}
            changeScreen={changeScreen}
          />
        </Box>
        <Box ref={InviteTeamMemberScreen}>
          <QuickstartInviteTeamMembers
            hasTeamMembers={
              userList
                ? checkIfHasOtherUsers(userList.account_settings_user_list)
                : false
            }
            screenNum={screenNum}
            maxScreenNum={MAX_SCREEN_NUMBER}
            changeScreen={changeScreen}
          />
        </Box>
        <Box ref={ProgramCustomizationScreen}>
          <QuickstartProgramCustomization
            slug={
              companySlug
                ? companySlug
                : `${localStorage.getItem('companySlug')}`
            }
            screenNum={screenNum}
            maxScreenNum={MAX_SCREEN_NUMBER}
            changeScreen={changeScreen}
          />
        </Box>
        <Box ref={ProgramCommunicationScreen}>
          <QuickstartProgramCommunication
            screenNum={screenNum}
            maxScreenNum={MAX_SCREEN_NUMBER}
            changeScreen={changeScreen}
          />
        </Box>
      </Stack>
      <Box sx={{ position: 'absolute', bottom: 0, right: 0 }}>
        <LanguageSelector variant='light' />
      </Box>
      </Stack>
  );
};

export default CustomerQuickStart;
